import { openDocumentOnClick } from '../../helper/documentHelper';
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TableProps } from 'antd';
import {
  Avatar,
  Button,
  Card,
  Col,
  Empty,
  Flex,
  Input,
  Row,
  Statistic,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  MinusCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { SafetyDocumentInfo } from '../../Types/interfaces';
import { Roles, StepStatus } from '../../Types/interfaces';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useHasRole } from '../../hooks/useHasRole';
import { GetSafetyDocumentsForLegalDepartmentMember } from '../../helper/apiHelper';

const EMPTY_SAFETY_DOCUMENTS: SafetyDocumentInfo[] = [];

// how each state of the work safety step is shown
const STATUS_DISPLAY: Record<
  StepStatus,
  { color: string; icon: React.ReactNode; label: string }
> = {
  [StepStatus.notStarted]: {
    color: 'default',
    icon: <MinusCircleOutlined />,
    label: 'safetyDocumentTable.notRequested',
  },
  [StepStatus.initialized]: {
    color: 'processing',
    icon: <ClockCircleOutlined />,
    label: 'safetyDocumentTable.requested',
  },
  [StepStatus.updated]: {
    color: 'warning',
    icon: <CloudUploadOutlined />,
    label: 'safetyDocumentTable.uploaded',
  },
  [StepStatus.finished]: {
    color: 'success',
    icon: <CheckCircleOutlined />,
    label: 'safetyDocumentTable.finalized',
  },
};

const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

export const SafetyDocumentTable = (): React.ReactElement => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const currentUser = useCurrentUser();
  const hasRole = useHasRole();
  const isAuthorised = hasRole(Roles.LegalDepartment);
  const [search, setSearch] = useState('');

  const {
    data: documentData = EMPTY_SAFETY_DOCUMENTS,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [...QUERY_KEYS.SAFETY_DOCUMENTS, currentUser.id],
    queryFn: () => GetSafetyDocumentsForLegalDepartmentMember(currentUser.id),
    enabled: isAuthorised,
  });

  const filteredData = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return documentData;
    }
    return documentData.filter(
      (document) =>
        document.studentName.toLowerCase().includes(term) ||
        document.studentEmail.toLowerCase().includes(term)
    );
  }, [documentData, search]);

  const waitingCount = documentData.filter(
    (document) =>
      document.documentState === StepStatus.initialized ||
      document.documentState === StepStatus.updated
  ).length;
  const finalizedCount = documentData.filter(
    (document) => document.documentState === StepStatus.finished
  ).length;

  const columns: TableProps<SafetyDocumentInfo>['columns'] = [
    {
      title: t('safetyDocumentTable.student'),
      key: 'student',
      sorter: (a, b) => a.studentName.localeCompare(b.studentName),
      defaultSortOrder: 'ascend',
      render: (_, record) => (
        <Flex gap={12} align="center">
          <Avatar
            style={{
              backgroundColor: token.colorPrimaryBg,
              color: token.colorPrimary,
              flex: 'none',
            }}
          >
            {getInitials(record.studentName)}
          </Avatar>
          <Flex vertical>
            <Typography.Text strong>{record.studentName}</Typography.Text>
            <Typography.Link
              href={`mailto:${record.studentEmail}`}
              type="secondary"
            >
              {record.studentEmail}
            </Typography.Link>
          </Flex>
        </Flex>
      ),
    },
    {
      title: t('safetyDocumentTable.state'),
      dataIndex: 'documentState',
      key: 'documentState',
      width: 200,
      filters: Object.entries(STATUS_DISPLAY).map(([value, display]) => ({
        text: t(display.label),
        value: Number(value),
      })),
      onFilter: (value, record) => record.documentState === value,
      sorter: (a, b) => a.documentState - b.documentState,
      render: (status: StepStatus) => {
        const display =
          STATUS_DISPLAY[status] ?? STATUS_DISPLAY[StepStatus.notStarted];
        return (
          <Tag color={display.color} icon={display.icon}>
            {t(display.label)}
          </Tag>
        );
      },
    },
    {
      title: t('safetyDocumentTable.document'),
      dataIndex: 'documentUrl',
      key: 'documentUrl',
      width: 200,
      render: (url: string) =>
        url ? (
          <Button
            type="link"
            icon={<FileTextOutlined />}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={openDocumentOnClick(url)}
            style={{ paddingInline: 0 }}
          >
            {t('safetyDocumentTable.documentLink')}
          </Button>
        ) : (
          <Typography.Text type="secondary">
            {t('safetyDocumentTable.noDocument')}
          </Typography.Text>
        ),
    },
  ];

  return (
    <Card>
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12}>
          <Flex vertical>
            <Typography.Title level={4} style={{ margin: 0 }}>
              {t('safetyDocumentTable.title')}
            </Typography.Title>
            <Typography.Text type="secondary">
              {t('safetyDocumentTable.description')}
            </Typography.Text>
          </Flex>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isFetching && !isLoading}
          >
            {t('safetyDocumentTable.refresh')}
          </Button>
        </Flex>

        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title={t('safetyDocumentTable.total')}
                value={documentData.length}
                prefix={<FileTextOutlined />}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title={t('safetyDocumentTable.waiting')}
                value={waitingCount}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: token.colorPrimary }}
                loading={isLoading}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title={t('safetyDocumentTable.finalizedCount')}
                value={finalizedCount}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: token.colorSuccess }}
                loading={isLoading}
              />
            </Card>
          </Col>
        </Row>

        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={t('safetyDocumentTable.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ maxWidth: 360 }}
        />

        <Table<SafetyDocumentInfo>
          rowKey="studentId"
          columns={columns}
          dataSource={filteredData}
          loading={isLoading}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 10,
            hideOnSinglePage: true,
            showTotal: (total) =>
              t('safetyDocumentTable.paginationTotal', { count: total }),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  search
                    ? t('safetyDocumentTable.noResults')
                    : t('safetyDocumentTable.empty')
                }
              />
            ),
          }}
        />
      </Flex>
    </Card>
  );
};
